import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Button, Checkbox, CssBaseline, FormControlLabel, LinearProgress, TextField, ThemeProvider, Typography, createTheme, useMediaQuery,
} from '@mui/material';
import { keyframes } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import App from './App';
import { BOT_API_URL } from './config.js';
import { hasValidSession, saveAccount } from './api.js';
import DisboxFileManager from './disbox-file-manager';

// Halaman /web/drive: pintu masuk file manager yang bisa dibuka dari mana
// saja (lewat domain publik). Hanya login (tanpa Sign Up). Dari luar kantor,
// setelah password benar bot mengirim kode 6 digit lewat DM Discord, dan
// kode itu wajib dimasukkan (lihat /auth/login di discord-bot/index.js).
export default function DrivePage() {
    const location = useLocation();
    // Dievaluasi ulang tiap pindah alamat: sesi habis/logout -> kembali ke form login.
    const loggedIn = hasValidSession();
    useEffect(() => {
        document.title = loggedIn ? 'Disbox Drive' : 'Masuk - Disbox Drive';
    }, [loggedIn]);
    if (loggedIn) return <App driveMode key="app" />;
    return <DriveLogin key={location.key} />;
}

// Tampilan mengikuti pola halaman login akun Google: latar abu-abu, kartu
// membulat, dua kolom di layar lebar, langkah username lalu password, tombol
// pil "Berikutnya". Mode gelap mengikuti setelan perangkat.
function makeTheme(dark) {
    return createTheme({
        palette: dark
            ? {
                mode: 'dark',
                primary: { main: '#a8c7fa', contrastText: '#062e6f' },
                background: { default: '#131314', paper: '#0e0e0e' },
                text: { primary: '#e3e3e3', secondary: '#c4c7c5' },
                error: { main: '#f2b8b5' },
                divider: '#8e918f',
            }
            : {
                mode: 'light',
                primary: { main: '#0b57d0', contrastText: '#ffffff' },
                background: { default: '#f0f4f9', paper: '#ffffff' },
                text: { primary: '#1f1f1f', secondary: '#444746' },
                error: { main: '#b3261e' },
                divider: '#747775',
            },
        shape: { borderRadius: 4 },
        typography: { fontFamily: '"Google Sans", Roboto, "Segoe UI", Arial, sans-serif' },
        components: {
            MuiButton: { styleOverrides: { root: { textTransform: 'none', borderRadius: 100, fontWeight: 500, fontSize: '0.875rem', letterSpacing: '0.01em' } } },
        },
    });
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

async function requestLogin(body) {
    let response;
    try {
        response = await fetch(`${BOT_API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            credentials: 'omit',
            cache: 'no-store',
        });
    } catch {
        throw new Error('Server Disbox tidak bisa dihubungi. Coba lagi sebentar lagi.');
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(data.error || `Gagal masuk (status ${response.status}).`);
        error.mfa = Boolean(data.mfa);
        error.challenge = data.challenge || null;
        error.resendIn = Number(data.resendIn) || 0;
        error.expired = Boolean(data.expired);
        error.status = response.status;
        throw error;
    }
    return data;
}

// Logo Disbox (kotak biru membulat dengan "D"), sama dengan aplikasi desktop.
function DisboxLogo({ size = 48 }) {
    return (
        <Box aria-hidden sx={{
            width: size, height: size, borderRadius: `${size * 0.22}px`, display: 'grid', placeItems: 'center',
            bgcolor: '#1f6fe5', color: '#fff', fontWeight: 700, fontSize: size * 0.58, lineHeight: 1, userSelect: 'none',
        }}>
            D
        </Box>
    );
}

// Chip akun di bawah judul (seperti alamat email di login Google). Klik = ganti akun.
function AccountChip({ username, onClick, disabled }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={`Ganti akun (sekarang: ${username})`}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, mt: 2.5, pl: 0.5, pr: 1, py: 0.5,
                border: 1, borderColor: 'divider', borderRadius: 100, bgcolor: 'transparent', color: 'text.primary',
                font: 'inherit', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', maxWidth: '100%',
                '&:hover': { bgcolor: 'action.hover' },
                '&:disabled': { cursor: 'default', opacity: 0.6 },
            }}
        >
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#1f6fe5', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                {(username[0] || '?').toUpperCase()}
            </Box>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{username}</Box>
            <ExpandMoreIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        </Box>
    );
}

function ErrorText({ children }) {
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.75 }}>
            <ErrorOutlineIcon sx={{ fontSize: 16, mt: '1px' }} />
            <span>{children}</span>
        </Box>
    );
}

function DriveLogin() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const dark = useMediaQuery('(prefers-color-scheme: dark)');
    const theme = useMemo(() => makeTheme(dark), [dark]);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [step, setStep] = useState('username'); // 'username' | 'password' | 'code' | 'loading'
    const [code, setCode] = useState('');
    const [challenge, setChallenge] = useState(null);
    const [resendAt, setResendAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const notice = params.has('expired') || params.has('relogin') ? 'Sesi login sudah habis. Silakan masuk lagi.' : null;
    const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));

    useEffect(() => {
        if (step !== 'code') return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [step]);

    const finish = async (account) => {
        setStep('loading');
        saveAccount({
            username: account.username,
            userId: account.userId,
            webhookUrls: account.webhookUrls,
            uploadWebhooks: account.uploadWebhooks,
            token: account.token,
            tokenExpiresAt: account.tokenExpiresAt,
        });
        setPassword('');
        setCode('');
        try {
            const manager = await DisboxFileManager.create(account.webhookUrls);
            await manager.syncFromDiscordIfNewer();
        } catch (e) {
            console.warn('[Disbox] Sinkronisasi awal gagal:', e);
        }
        navigate('/drive', { replace: true });
    };

    // otp kosong = minta kode (baru) lewat DM; berisi = kirim kode untuk diverifikasi.
    const submit = async (otp) => {
        setBusy(true);
        setError(null);
        try {
            const body = { username: username.trim(), password };
            if (otp) {
                body.code = otp;
                body.challenge = challenge;
            }
            await finish(await requestLogin(body));
        } catch (e) {
            if (e.mfa && e.challenge && !otp) {
                // Kode dikirim (atau baru saja dikirim) lewat DM Discord.
                setChallenge(e.challenge);
                setResendAt(Date.now() + e.resendIn * 1000);
                setNow(Date.now());
                setCode('');
                setStep('code');
            } else {
                setError(e.message);
                if (otp) setCode('');
                if (e.expired) setResendAt(0);
            }
        } finally {
            setBusy(false);
        }
    };

    const goTo = (next) => {
        setError(null);
        setStep(next);
    };

    const onSubmit = (event) => {
        event.preventDefault();
        if (busy) return;
        if (step === 'username') {
            if (!username.trim()) {
                setError('Masukkan username.');
                return;
            }
            goTo('password');
        } else if (step === 'password') {
            if (!password) {
                setError('Masukkan sandi.');
                return;
            }
            submit(null);
        } else if (step === 'code') {
            if (code.length !== 6) {
                setError('Kode terdiri dari 6 angka.');
                return;
            }
            submit(code);
        }
    };

    const switchAccount = () => {
        setPassword('');
        setCode('');
        setChallenge(null);
        goTo('username');
    };

    const titles = {
        username: ['Masuk', 'Lanjutkan ke Disbox Drive'],
        password: ['Selamat datang', null],
        code: ['Verifikasi 2 langkah', 'Bot Disbox baru saja mengirim kode 6 digit lewat DM Discord. Pesan kode hilang otomatis setelah 5 menit.'],
        loading: ['Selamat datang', 'Menyiapkan file kamu...'],
    };
    const [title, subtitle] = titles[step];

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                px: { xs: 0, sm: 2 },
                py: { xs: 0, sm: 4 },
            }}>
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: { sm: 450, md: 1040 },
                    minHeight: { xs: 'calc(100vh - 48px)', sm: 'auto' },
                    bgcolor: 'background.paper',
                    borderRadius: { xs: 0, sm: '28px' },
                    overflow: 'hidden',
                }}>
                    {(busy || step === 'loading') && (
                        <LinearProgress aria-label="Memproses" sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4 }} />
                    )}

                    <Box sx={{
                        p: { xs: 3, sm: 4.5 },
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        columnGap: 4,
                        rowGap: 3,
                        minHeight: { md: 384 },
                        opacity: busy ? 0.7 : 1,
                        transition: 'opacity .2s',
                    }}>
                        <Box>
                            <DisboxLogo size={48} />
                            <Typography component="h1" sx={{ mt: 3, fontSize: { xs: '2rem', md: '2.25rem' }, lineHeight: 1.22, fontWeight: 400, color: 'text.primary' }}>
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography sx={{ mt: 2, fontSize: '1rem', color: 'text.primary', maxWidth: 400 }}>{subtitle}</Typography>
                            )}
                            {step !== 'username' && (
                                <AccountChip username={username.trim()} onClick={switchAccount} disabled={busy || step === 'loading'} />
                            )}
                            {notice && step === 'username' && !error && (
                                <Typography sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>{notice}</Typography>
                            )}
                        </Box>

                        {step !== 'loading' && (
                            <Box
                                component="form"
                                onSubmit={onSubmit}
                                noValidate
                                key={step}
                                sx={{ display: 'flex', flexDirection: 'column', pt: { md: 9 }, animation: `${fadeIn} .25s ease-out` }}
                            >
                                {step === 'username' && (
                                    <>
                                        <TextField
                                            fullWidth
                                            label="Username"
                                            name="username"
                                            value={username}
                                            onChange={(e) => { setUsername(e.target.value); setError(null); }}
                                            autoComplete="username"
                                            autoCapitalize="none"
                                            spellCheck={false}
                                            autoFocus
                                            error={Boolean(error)}
                                            helperText={error ? <ErrorText>{error}</ErrorText> : ' '}
                                            inputProps={{ maxLength: 64 }}
                                        />
                                        <Typography sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                                            Akun Disbox dibuat oleh admin. Lupa username atau sandi? Hubungi admin Disbox.
                                        </Typography>
                                    </>
                                )}

                                {step === 'password' && (
                                    <>
                                        {/* Username ikut di form ini supaya pengelola sandi browser menyimpannya bersama sandinya. */}
                                        <input type="text" name="username" autoComplete="username" value={username} readOnly tabIndex={-1} aria-hidden
                                            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
                                        <TextField
                                            fullWidth
                                            label="Masukkan sandi"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => { setPassword(e.target.value); setError(null); }}
                                            autoComplete="current-password"
                                            autoFocus
                                            disabled={busy}
                                            error={Boolean(error)}
                                            helperText={error ? <ErrorText>{error}</ErrorText> : ' '}
                                            inputProps={{ maxLength: 256 }}
                                        />
                                        <FormControlLabel
                                            sx={{ mt: 0.5, ml: -1 }}
                                            control={<Checkbox checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} disabled={busy} />}
                                            label={<Typography sx={{ fontSize: '0.875rem' }}>Tampilkan sandi</Typography>}
                                        />
                                    </>
                                )}

                                {step === 'code' && (
                                    <>
                                        <TextField
                                            fullWidth
                                            label="Masukkan kode"
                                            name="one-time-code"
                                            value={code}
                                            onChange={(e) => {
                                                const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setCode(next);
                                                setError(null);
                                                if (next.length === 6 && !busy) submit(next);
                                            }}
                                            autoComplete="one-time-code"
                                            autoFocus
                                            disabled={busy}
                                            error={Boolean(error)}
                                            helperText={error ? <ErrorText>{error}</ErrorText> : ' '}
                                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, style: { letterSpacing: '0.3em', fontSize: '1.125rem' } }}
                                        />
                                        <Box sx={{ mt: 0.5 }}>
                                            <Button variant="text" disabled={busy || resendIn > 0} onClick={() => submit(null)} sx={{ px: 1.5, ml: -1.5 }}>
                                                {resendIn > 0 ? `Kirim kode baru (${resendIn} dtk)` : 'Kirim kode baru'}
                                            </Button>
                                        </Box>
                                    </>
                                )}

                                <Box sx={{ flex: 1, minHeight: 32 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    {step !== 'username' && (
                                        <Button variant="text" disabled={busy} onClick={step === 'code' ? () => goTo('password') : switchAccount} sx={{ px: 1.5 }}>
                                            {step === 'code' ? 'Kembali' : 'Ganti akun'}
                                        </Button>
                                    )}
                                    <Button type="submit" variant="contained" disableElevation disabled={busy} sx={{ px: 3, py: 1.1, minWidth: 112 }}>
                                        Berikutnya
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box sx={{
                    width: '100%',
                    maxWidth: { sm: 450, md: 1040 },
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    px: { xs: 3, sm: 1 },
                    py: 1.5,
                    color: 'text.secondary',
                }}>
                    <Typography sx={{ fontSize: '0.75rem' }}>Disbox Drive</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HttpsOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.75rem' }}>Koneksi terenkripsi, kode verifikasi lewat Discord</Typography>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
